import { useEffect } from 'react';
import { isConversationActive } from '../helpers/conversation-helpers';
import { isParticipantIdentityAssignedToUser } from '../helpers/conversation-participant-helpers';
import { getConversationFilterFromParticipantIdentity } from '../helpers/conversations-filter-helpers';
import { ConversationsFilter } from '../types';
import { useMessagingStore } from './use-messaging-store';
import { logWarn } from '../../monitoring/log';

// This hook recognizes when the selected conversation participant filter needs to change, and changes it as needed.
//
// It determines this by inspecting 3 things:
//
// 1. the currently selected filter
// 2. the currently selected conversation
// 3. the user's active participant identities (this is the value that changes)
//
// If there's a discrepency between the selected filter and the conversation / identities the filter is updated
// The useEffect triggers when the user's participant identities are updated.

export const useSwitchFilterAfterIdentityChange = () => {
    const {
        participantIdentities,
        selectedConversationFilter,
        selectedConversationKey,
        setSelectedConversationFilter,
    } = useMessagingStore();

    useEffect(() => {
        if (!selectedConversationFilter || !participantIdentities) {
            return;
        }

        const { filterType, filterParticipant } = selectedConversationFilter;

        const inputs = {
            isAllActiveFilterSelected: filterType === 'ALL_ACTIVE',
            isActiveIdentityFilterSelected:
                filterType !== 'INACTIVE' && filterType !== 'ALL_ACTIVE',
            isInactiveIdentityFilterSelected: filterType === 'INACTIVE',
            isConversationSelected:
                selectedConversationKey !== undefined &&
                selectedConversationKey.participantIdentity !== undefined,
            isSelectedConversationActive: isConversationActive(
                selectedConversationKey,
                participantIdentities
            ),
            isUserAssignedToSelectedConversation:
                filterParticipant !== null &&
                isParticipantIdentityAssignedToUser(filterParticipant, participantIdentities),
        };

        const switchFilterActions = [
            {
                // user has:
                // * the 'all' filter selected
                // * an active conversation selected
                //
                // trigger:
                // * the participant is unassigned from the user
                // * conversation transitions to inactive
                //
                // action:
                // * select the new inactive participant filter
                condition:
                    inputs.isAllActiveFilterSelected &&
                    inputs.isConversationSelected &&
                    !inputs.isSelectedConversationActive,
                execute: () => {
                    selectConversationFilter({
                        filterType: 'INACTIVE',
                        filterParticipant: selectedConversationKey?.participantIdentity,
                    });
                },
            },
            {
                // user has:
                // * an active participant filter selected
                // * an active conversation selected
                //
                // trigger:
                // * the participant is unassigned from the user
                // * conversation transitions to inactive
                //
                // action:
                // * select the new inactive participant filter
                condition:
                    inputs.isActiveIdentityFilterSelected &&
                    inputs.isConversationSelected &&
                    !inputs.isSelectedConversationActive &&
                    !inputs.isUserAssignedToSelectedConversation,
                execute: () => {
                    selectConversationFilter({
                        filterType: 'INACTIVE',
                        filterParticipant: selectedConversationKey?.participantIdentity,
                    });
                },
            },
            {
                // user has:
                // * an active participant filter selected
                // * no conversation selected
                //
                // trigger:
                // * the participant is unassigned from the user
                //
                // action:
                // * select the 'All' filter
                condition:
                    inputs.isActiveIdentityFilterSelected &&
                    !inputs.isConversationSelected &&
                    !inputs.isUserAssignedToSelectedConversation,
                execute: () => {
                    selectConversationFilter({
                        filterType: 'ALL_ACTIVE',
                        filterParticipant: null,
                    });
                },
            },
            {
                // user has:
                // * an inactive participant filter selected
                // * an inactive conversation selected
                //
                // trigger:
                // * the participant is assigned to the user
                // * conversation transitions to active
                //
                // action:
                // * select the newly active participant filter
                condition:
                    inputs.isInactiveIdentityFilterSelected &&
                    inputs.isConversationSelected &&
                    inputs.isSelectedConversationActive &&
                    inputs.isUserAssignedToSelectedConversation,
                execute: () => {
                    if (selectedConversationKey?.participantIdentity?.entityType) {
                        selectConversationFilter({
                            filterType: selectedConversationKey.participantIdentity.entityType,
                            filterParticipant: selectedConversationKey.participantIdentity,
                        });
                    }
                },
            },
            {
                // user has:
                // * an inactive participant filter selected
                // * no conversation selected
                //
                // trigger:
                // * the participant is assigned to the user
                //
                // action:
                // * select the newly active participant filter
                condition:
                    inputs.isInactiveIdentityFilterSelected &&
                    !inputs.isConversationSelected &&
                    inputs.isUserAssignedToSelectedConversation,
                execute: () => {
                    if (filterParticipant === null) {
                        // note this isn't possible based on the condition but the type guard check isn't smart enough.
                        logWarn('Messaging panel: filterParticipant unexpectedly null');
                        return;
                    }
                    const conversationFilter =
                        getConversationFilterFromParticipantIdentity(filterParticipant);
                    selectConversationFilter(conversationFilter);
                },
            },
        ];

        // selectConversationFilter is used to avoid type errors below. It's not wonderful but it's better than using
        // ts-error.
        const selectConversationFilter = ({
            filterType,
            filterParticipant,
        }: {
            filterType: ConversationsFilter['filterType'];
            filterParticipant?: ConversationsFilter['filterParticipant'];
        }) => {
            if (filterType === 'ALL_ACTIVE' || filterParticipant === null) {
                setSelectedConversationFilter({
                    filterType: 'ALL_ACTIVE',
                    filterParticipant: null,
                });
            } else {
                if (filterParticipant === undefined) {
                    // note this isn't possible based on the actions condition but the type guard check isn't smart
                    // enough.
                    logWarn('Messaging panel: filterParticipant unexpectedly undefined');
                    return;
                }
                setSelectedConversationFilter({
                    filterType,
                    filterParticipant,
                });
            }
        };

        const switchFilterAction = switchFilterActions.find((action) => action.condition);
        switchFilterAction?.execute();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantIdentities]);
};
